import { useEffect, useRef } from "react";

import { getPushPublicKey, registerPushSubscription } from "../services/api";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function useServiceWorker(token: string | undefined) {
  const registered = useRef(false);

  useEffect(() => {
    if (!token || registered.current) return;
    if (!("serviceWorker" in navigator)) return;

    registered.current = true;

    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        await reg.update();

        if (!("PushManager" in window)) return;

        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          await sendSubscriptionToServer(token, existing);
          return;
        }

        let publicKey: string;
        try {
          const res = await getPushPublicKey(token);
          publicKey = res.public_key;
        } catch {
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });

        await sendSubscriptionToServer(token, sub);
      } catch {
        // SW registration can fail in some environments (HTTP, incognito)
      }
    })();
  }, [token]);
}

async function sendSubscriptionToServer(token: string, sub: PushSubscription) {
  const key = sub.getKey("p256dh");
  const auth = sub.getKey("auth");
  if (!key || !auth) return;

  await registerPushSubscription(token, {
    endpoint: sub.endpoint,
    p256dh_key: btoa(String.fromCharCode(...new Uint8Array(key))),
    auth_key: btoa(String.fromCharCode(...new Uint8Array(auth))),
    user_agent: navigator.userAgent,
  });
}
